/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The FAQPage JSON-LD of an article's FAQ blocks, typed for the client
 * (ArticlePage after hydration, the admin's FAQ block preview). The builder is
 * buildArticleFAQJsonLd in src/lib/articleSeo.js — the one the baked head uses,
 * so the node a page is baked with and the node it replaces it with after
 * hydration come from one function. Until 2026-10-14 this file held a second
 * copy of it.
 */

import type { ArticleContentBlock } from '../pages/admin/articleTypes';
import { buildArticleFAQJsonLd } from './articleSeo.js';

interface FAQSchemaQuestion {
  '@type': 'Question';
  name: string;
  acceptedAnswer: { '@type': 'Answer'; text: string };
}

export interface FAQSchemaPayload {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  inLanguage: string;
  mainEntity: FAQSchemaQuestion[];
}

/**
 * Builds a FAQPage JSON-LD payload from every `kind: 'faq'` block in the
 * article. Returns null when no item qualifies — caller should remove any
 * existing script tag in that case. Question text is plain (HTML stripped);
 * answer text keeps HTML, which Google explicitly allows for FAQPage
 * `acceptedAnswer`.
 */
export function buildArticleFAQSchema(
  blocks: ArticleContentBlock[] | undefined,
  language: 'nl' | 'en'
): FAQSchemaPayload | null {
  return buildArticleFAQJsonLd(blocks, language) as FAQSchemaPayload | null;
}
